import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import AnnouncementDetailModal from '../../announcements/AnnouncementDetailModal';
import { announcementService } from '../../../services/announcementService';
import { canManageSociety } from '../../../utils/societyPermissions';
import SocietySectionToolbar from './SocietySectionToolbar';

const SocietyAnnouncements = ({ society, onError }) => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const { showError } = useToast();

  const canCreateAnnouncement =
    isAuthenticated &&
    user &&
    (user.is_staff ||
      user.is_superuser ||
      user.role === 'admin' ||
      user.role === 'moderator' ||
      canManageSociety(user, society));

  const loadAnnouncements = useCallback(async () => {
    if (!society?.id) return;
    try {
      setLoading(true);
      setError(null);
      
      const response = await announcementService.getSocietyAnnouncements(society.id, {
        page: 1,
        page_size: 10
      });
      
      setAnnouncements(response.results || []);
      setHasMore(response.next !== null);
      setPage(1);
    } catch (err) {
      console.error('Error loading announcements:', err);
      const errorMessage = err.message || 'Failed to load announcements';
      setError(errorMessage);
      if (onError) onError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [society?.id, onError, showError]);

  // Load announcements
  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  // Load more announcements
  const loadMore = async () => {
    if (loading || !hasMore) return;

    try {
      setLoading(true);
      const response = await announcementService.getSocietyAnnouncements(society.id, {
        page: page + 1,
        page_size: 10
      });
      
      setAnnouncements(prev => [...prev, ...(response.results || [])]);
      setHasMore(response.next !== null);
      setPage(prev => prev + 1);
    } catch (err) {
      console.error('Error loading more announcements:', err);
      showError(err.message || 'Failed to load more announcements');
    } finally {
      setLoading(false);
    }
  };

  // Track announcement click
  const handleAnnouncementClick = async (announcementId) => {
    try {
      await announcementService.trackClick(announcementId);
    } catch (err) {
      console.error('Error tracking click:', err);
    }
  };

  // Acknowledge announcement
  const handleAcknowledge = async (announcementId) => {
    try {
      await announcementService.acknowledge(announcementId);
      // Update local state to reflect acknowledgment
      setAnnouncements(prev => 
        prev.map(announcement => 
          announcement.id === announcementId 
            ? { ...announcement, user_has_acknowledged: true }
            : announcement
        )
      );
    } catch (err) {
      console.error('Error acknowledging announcement:', err);
      showError(err.message || 'Failed to acknowledge announcement');
    }
  };

  // Handle read more click
  const handleReadMore = (announcement) => {
    setSelectedAnnouncement(announcement);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedAnnouncement(null);
  };

  // Handle acknowledgment from modal
  const handleAcknowledgment = (announcementId) => {
    setAnnouncements(prev => 
      prev.map(announcement => 
        announcement.id === announcementId 
          ? { ...announcement, user_has_acknowledged: true, acknowledgment_count: (announcement.acknowledgment_count || 0) + 1 }
          : announcement
      )
    );
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get type color
  const getTypeColor = (type) => {
    switch (type) {
      case 'info':
        return 'text-blue-600';
      case 'warning':
        return 'text-yellow-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      case 'maintenance':
        return 'text-purple-600';
      case 'update':
        return 'text-indigo-600';
      case 'event':
        return 'text-pink-600';
      case 'promotion':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && announcements.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="medium" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          <div className="text-center py-8">
            <div className="text-red-600 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Announcements</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadAnnouncements}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <SocietySectionToolbar
        title="Announcements"
        count={announcements.length}
        itemLabel={{ singular: 'announcement', plural: 'announcements' }}
        societyName={society?.name}
        addButton={
          canCreateAnnouncement
            ? {
                label: 'Create Announcement',
                onClick: () => navigate(`/create-announcement?society=${society.id}`),
              }
            : null
        }
      />

      <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-6">
        {announcements.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V1a1 1 0 011-1h2a1 1 0 011 1v18a1 1 0 01-1 1H4a1 1 0 01-1-1V1a1 1 0 011-1h2a1 1 0 011 1v3m0 0H5a1 1 0 00-1 1v16a1 1 0 001 1h14a1 1 0 001-1V5a1 1 0 00-1-1h-2" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Announcements</h3>
            <p className="text-gray-600">
              There are no announcements for {society.name} at the moment.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {announcements.map((announcement) => (
              <div
                key={announcement.id}
                className={`border rounded-lg p-4 hover:shadow-md transition-shadow duration-200 ${
                  announcement.show_in_header ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {announcement.title}
                      </h3>
                      {announcement.icon && (
                        <span className="text-gray-500">
                          <i className={announcement.icon}></i>
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-4 mb-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(announcement.priority)}`}>
                        {announcement.priority}
                      </span>
                      <span className={`text-sm font-medium ${getTypeColor(announcement.announcement_type)}`}>
                        {announcement.announcement_type}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatDate(announcement.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  {announcement.show_in_header && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Featured
                    </span>
                  )}
                </div>

                {announcement.summary && (
                  <p className="text-gray-700 mb-3">{announcement.summary}</p>
                )}

                <div 
                  className="text-gray-800 mb-4 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: announcement.content }}
                />

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>Views: {announcement.view_count || 0}</span>
                    <span>Clicks: {announcement.click_count || 0}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {announcement.requires_acknowledgment && !announcement.user_has_acknowledged && isAuthenticated && (
                      <button
                        onClick={() => handleAcknowledge(announcement.id)}
                        className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700"
                      >
                        Acknowledge
                      </button>
                    )}
                    
                    <button
                      onClick={() => {
                        handleAnnouncementClick(announcement.id);
                        handleReadMore(announcement);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Read More
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {hasMore && (
              <div className="text-center pt-4">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Announcement Detail Modal */}
      <AnnouncementDetailModal
        announcement={selectedAnnouncement}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onAcknowledge={handleAcknowledgment}
      />
    </div>
  );
};

export default SocietyAnnouncements;
